declare const canonicalVersionIDBrand: unique symbol;

export type CanonicalVersionID = string & {
	readonly [canonicalVersionIDBrand]: 'CanonicalVersionID';
};

export type EditUnitType = 'edit-session' | 'thread';

export type EditUnitIdentity = {
	type: EditUnitType;
	id: string;
};

export type ProjectScope = {
	kind: 'project';
	projectId: string;
};

export type CurrentEditUnitScope = EditUnitIdentity & {
	kind: 'current';
};

export type HistoricalEditUnitScope = EditUnitIdentity & {
	kind: 'historical';
	versionId: CanonicalVersionID;
};

export type EditorScope = ProjectScope | CurrentEditUnitScope | HistoricalEditUnitScope;

export const HISTORICAL_VERSION_QUERY_PARAM = 'version_id';
export const LEGACY_HISTORY_ITEM_QUERY_PARAM = 'history-item';
export const HISTORY_COMPARE_QUERY_PARAM = 'compare_to';
export const HISTORY_GROUP_BASE_QUERY_PARAM = 'group_base';

export function normalizeHistoryComparisonSelector(
	value: string | null | undefined,
): string | null {
	const normalized = value?.trim();
	return normalized ? normalized : null;
}

export const normalizeHistoryGroupBaseSelector = normalizeHistoryComparisonSelector;

export function canonicalVersionID(value: string): CanonicalVersionID {
	if (!value || value !== value.trim()) {
		throw new Error(
			'Canonical version ID must be a non-empty value without surrounding whitespace',
		);
	}
	if (value === 'latest') {
		throw new Error('"latest" is an internal persistence sentinel, not a public version ID');
	}
	return value as CanonicalVersionID;
}

export function currentEditUnitScope(identity: EditUnitIdentity): CurrentEditUnitScope {
	return { kind: 'current', ...validateEditUnitIdentity(identity) };
}

export function historicalEditUnitScope(
	identity: EditUnitIdentity,
	versionId: CanonicalVersionID,
): HistoricalEditUnitScope {
	return { kind: 'historical', ...validateEditUnitIdentity(identity), versionId };
}

export function sameEditUnit(a: EditUnitIdentity, b: EditUnitIdentity): boolean {
	return a.type === b.type && a.id === b.id;
}

export function editUnitScopeKey(scope: CurrentEditUnitScope | HistoricalEditUnitScope): string {
	return scope.kind === 'historical'
		? `${scope.type}:${scope.id}:version:${scope.versionId}`
		: `${scope.type}:${scope.id}:current`;
}

function validateEditUnitIdentity(identity: EditUnitIdentity): EditUnitIdentity {
	if (identity.type !== 'edit-session' && identity.type !== 'thread') {
		throw new Error(`Unsupported edit-unit type: ${String(identity.type)}`);
	}
	if (!identity.id) throw new Error('Edit-unit ID must not be empty');
	return identity;
}
